import { useRef, useState } from 'react';

import { type ResolvableModalFC } from '@/components/modals/modalsManager';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/Input';
import { YStack } from '@/components/ui/layout/layout';

interface InstanceUrlModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resolve: (mcpUrl: string | null) => void;
  title: string;
  description: string;
  label: string;
  placeholder: string;
  invalidMessage: string;
  /** Returns the MCP server URL for the typed value, or null when it is invalid. */
  toMcpUrl: (input: string) => string | null;
}

export const InstanceUrlModal: ResolvableModalFC<
  InstanceUrlModalProps,
  string | null
> = ({
  open,
  onOpenChange,
  resolve,
  title,
  description,
  label,
  placeholder,
  invalidMessage,
  toMcpUrl,
}) => {
  const [value, setValue] = useState('');
  const [showError, setShowError] = useState(false);
  const hasResolved = useRef(false);

  const resolveParent = (mcpUrl: string | null) => {
    hasResolved.current = true;
    resolve(mcpUrl);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !hasResolved.current) {
      resolveParent(null);
    }
    onOpenChange(nextOpen);
  };

  const handleContinue = () => {
    const mcpUrl = toMcpUrl(value);
    if (!mcpUrl) {
      setShowError(true);
      return;
    }
    onOpenChange(false);
    resolveParent(mcpUrl);
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content className="max-w-md">
        <Dialog.Header>
          <Dialog.Title>{title}</Dialog.Title>
          <Dialog.Description>{description}</Dialog.Description>
        </Dialog.Header>

        <YStack className="gap-2 py-3">
          <label
            htmlFor="instance-url"
            className="text-sm font-medium text-foreground"
          >
            {label}
          </label>
          <Input
            id="instance-url"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setShowError(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleContinue();
            }}
            placeholder={placeholder}
            className="text-sm"
            autoFocus
          />
          {showError && (
            <span className="text-xs break-all text-destructive">
              {invalidMessage}
            </span>
          )}
        </YStack>

        <Dialog.Footer className="gap-2">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              resolveParent(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleContinue} disabled={!value.trim()}>
            Continue
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};
